import React, { useState, useEffect, useMemo } from 'react';
import { FiEye, FiMail, FiTrash2, FiSearch, FiCheckCircle, FiDownload, FiActivity } from 'react-icons/fi';
import ConfirmModal from '../components/ConfirmModal';
import PageCountModal from '../components/PageCountModal';
import { getToken } from '../auth';

const AllFilesView = ({
  fileList,
  parseFilename,
  fieldLabels = [],
  fieldKeys = [],
  sortField,
  sortDirection,
  handleSort,
  API_URL,
  selectedRow,
  setSelectedRow,
  onEmail,
  onDelete,
  canDelete = false,
  isAdmin = false,
}) => {
  const [filterText, setFilterText] = useState(() => {
    try { return localStorage.getItem('allFiles.filterText') || ''; } catch (_) { return ''; }
  });
  // Persist filter text between sessions
  useEffect(() => {
    try { localStorage.setItem('allFiles.filterText', filterText); } catch (_) {}
  }, [filterText]);

  const [localQueued, setLocalQueued] = useState(() => new Set());
  const [delOpen, setDelOpen] = useState(false);
  const [delName, setDelName] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [zipProg, setZipProg] = useState({ active: false, processed: 0, total: 0 });
  const [batchOcr, setBatchOcr] = useState({ active: false, processed: 0, total: 0, errors: 0 });
  const [pageCounts, setPageCounts] = useState({
    open: false,
    loading: false,
    items: [],
    totalPages: 0,
    error: '',
    warnings: [],
  });
  const [auditModal, setAuditModal] = useState({
    open: false,
    loading: false,
    entries: [],
    error: '',
    name: '',
  });
  const [auditSort, setAuditSort] = useState({ field: 'timestamp', direction: 'desc' });
  // Pagination: rows per page persisted, current page in-memory
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('allFiles.rowsPerPage') || '50', 10);
      return Number.isFinite(v) && v > 0 ? v : 50;
    } catch (_) { return 50; }
  });
  const [page, setPage] = useState(0);
  const list = Array.isArray(fileList) ? fileList : [];
  const sortedFiles = [...list];
  const formatCount = (value) => {
    const num = Number(value || 0);
    if (!Number.isFinite(num)) return '0';
    return num.toLocaleString();
  };
  const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };
  const formatAuditActor = (entry) => {
    if (!entry) return 'Unknown';
    if (entry.actorType === 'system') return 'System';
    return entry.userName || entry.userLogin || (entry.userId ? `User #${entry.userId}` : 'Unknown');
  };
  const formatAuditMeta = (meta) => {
    if (!meta || typeof meta !== 'object') return '';
    try {
      return Object.entries(meta)
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
        .join(', ');
    } catch (_) {
      return '';
    }
  };
  const auditSortIndicator = (field) => (auditSort.field === field ? (auditSort.direction === 'asc' ? ' ▲' : ' ▼') : '');
  const changeAuditSort = (field) => {
    setAuditSort(prev => {
      if (prev.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field, direction: 'asc' };
    });
  };
  const sortedAuditEntries = useMemo(() => {
    const entries = Array.isArray(auditModal.entries) ? [...auditModal.entries] : [];
    const dir = auditSort.direction === 'asc' ? 1 : -1;
    entries.sort((a, b) => {
      let aVal;
      let bVal;
      if (auditSort.field === 'timestamp') {
        const at = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
        const bt = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
        aVal = at;
        bVal = bt;
      } else if (auditSort.field === 'action') {
        aVal = String(a?.action || '').toLowerCase();
        bVal = String(b?.action || '').toLowerCase();
      } else if (auditSort.field === 'actor') {
        aVal = formatAuditActor(a).toLowerCase();
        bVal = formatAuditActor(b).toLowerCase();
      } else {
        aVal = JSON.stringify(a?.meta || {});
        bVal = JSON.stringify(b?.meta || {});
      }
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
    return entries;
  }, [auditModal.entries, auditSort]);

  const auditPdfUrl = useMemo(() => {
    if (!auditModal.name) return null;
    const token = getToken();
    const qs = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${API_URL}/files/${encodeURIComponent(auditModal.name)}/audit.pdf${qs}`;
  }, [auditModal.name, API_URL]);

  const statusRank = { error: -1, processing: 0, queued: 1, missing: 2, ready: 3 };
  const getOcrStatus = (f) => {
    if (f.invalid || f.size === 0) return 'error';
    const isLocallyQueued = localQueued.has(f.filename);
    const hasText = !!f.hasText;
    return (f.processing ? 'processing' : ((f.queued || isLocallyQueued || f.forced) ? 'queued' : (hasText ? 'ready' : 'missing')));
  };

  if (sortField) {
    if (sortField === '__ocr__') {
      sortedFiles.sort((a, b) => {
        const sa = statusRank[getOcrStatus(a)] ?? 99;
        const sb = statusRank[getOcrStatus(b)] ?? 99;
        if (sa < sb) return sortDirection === 'asc' ? -1 : 1;
        if (sa > sb) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    } else if (sortField === '__created__') {
      sortedFiles.sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        const diff = (aTime || 0) - (bTime || 0);
        if (diff === 0) return 0;
        return sortDirection === 'asc' ? diff : -diff;
      });
    } else {
      sortedFiles.sort((a, b) => {
        const aVal = String(parseFilename(a.filename)[sortField] || '').toLowerCase();
        const bVal = String(parseFilename(b.filename)[sortField] || '').toLowerCase();
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
  }

  const t = filterText.trim().toLowerCase();
  const finalFiles = sortedFiles.filter(f => {
    if (!t) return true;
    const obj = parseFilename(f.filename);
    for (const k of fieldKeys) {
      if (String(obj[k] || '').toLowerCase().includes(t)) return true;
    }
    return false;
  });

  useEffect(() => {
    setLocalQueued((prev) => {
      if (!prev || prev.size === 0) return prev;
      const source = Array.isArray(fileList) ? fileList : [];
      const active = new Set(
        source
          .filter(f => f && (f.processing || f.queued || f.forced))
          .map(f => f.filename)
      );
      let changed = false;
      const next = new Set();
      for (const name of prev) {
        if (active.has(name)) {
          next.add(name);
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [fileList]);

  const queueSelectedForOcr = async () => {
    const targets = Array.from(selected);
    if (!targets.length || batchOcr.active) return;
    setBatchOcr({ active: true, processed: 0, total: targets.length, errors: 0 });
    let processed = 0;
    let errors = 0;
    let lastError = '';
    try { window.spartaToast && window.spartaToast(`Queuing OCR: 0/${targets.length}`, 'info', 0); } catch (_) {}
    const token = localStorage.getItem('auth_token') || '';
    for (const filename of targets) {
      try {
        const res = await fetch(`${API_URL}/files/${encodeURIComponent(filename)}/ocr`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ force: true })
        });
        if (res.ok) {
          setLocalQueued(prev => {
            const next = new Set(prev);
            next.add(filename);
            return next;
          });
        } else {
          errors += 1;
          const j = await res.json().catch(() => ({}));
          lastError = j?.message || lastError || 'Failed to enqueue some files for OCR';
        }
      } catch (err) {
        errors += 1;
        lastError = err?.message || lastError || 'Failed to enqueue some files for OCR';
      }
      processed += 1;
      setBatchOcr({ active: true, processed, total: targets.length, errors });
      try { window.spartaToast && window.spartaToast(`Queuing OCR: ${Math.min(processed, targets.length)}/${targets.length}`, 'info', 0); } catch (_) {}
    }
    setBatchOcr({ active: false, processed, total: targets.length, errors });
    if (errors === 0) {
      try { window.spartaToast && window.spartaToast(`Queued OCR for ${targets.length} file(s)`, 'success', 2500); } catch (_) {}
    } else {
      try { window.spartaToast && window.spartaToast(`OCR queue completed with ${errors} error(s)`, 'error', 3500); } catch (_) {}
      if (lastError) alert(lastError);
    }
  };

  const closePageCountModal = () => {
    setPageCounts(prev => ({ ...prev, open: false }));
  };

  const loadAuditTrail = async (filename) => {
    try {
      const headers = { Accept: 'application/json' };
      const token = getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/files/${encodeURIComponent(filename)}/audit`, { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.message || 'Failed to load audit trail');
      }
      const entries = Array.isArray(body.entries) ? body.entries : [];
      setAuditModal(prev => ({ ...prev, loading: false, entries, error: '' }));
    } catch (err) {
      setAuditModal(prev => ({ ...prev, loading: false, error: err.message || 'Failed to load audit trail' }));
    }
  };

  const openAuditTrail = (filename) => {
    setAuditModal({ open: true, loading: true, entries: [], error: '', name: filename });
    setAuditSort({ field: 'timestamp', direction: 'desc' });
    loadAuditTrail(filename);
  };

  const closeAuditTrail = () => {
    setAuditModal(prev => ({ ...prev, open: false }));
  };

  const handlePageCountClick = async () => {
    const targets = Array.from(selected);
    if (!targets.length || pageCounts.loading) return;
    setPageCounts({ open: true, loading: true, items: [], totalPages: 0, error: '', warnings: [] });
    try {
      const token = localStorage.getItem('auth_token') || '';
      const res = await fetch(`${API_URL}/files/page-counts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ filenames: targets }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.message || 'Failed to fetch page counts');
      }
      const items = Array.isArray(body?.items) ? body.items : [];
      const totalPages = Number(body?.totalPages || 0) || items.reduce((sum, item) => sum + (Number(item?.pages || 0) || 0), 0);
      setPageCounts({
        open: true,
        loading: false,
        items,
        totalPages,
        error: '',
        warnings: Array.isArray(body?.errors) ? body.errors : [],
      });
    } catch (e) {
      setPageCounts(prev => ({ ...prev, loading: false, error: e?.message || 'Failed to fetch page counts' }));
    }
  };

  // Persist rows-per-page
  useEffect(() => {
    try { localStorage.setItem('allFiles.rowsPerPage', String(rowsPerPage)); } catch (_) {}
  }, [rowsPerPage]);

  // Clamp/reset page on changes
  const totalPages = Math.max(1, Math.ceil(finalFiles.length / Math.max(1, rowsPerPage)));
  useEffect(() => {
    if (page > totalPages - 1) setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, filterText, sortField, sortDirection, finalFiles.length]);

  const startIdx = page * rowsPerPage;
  const endIdx = Math.min(finalFiles.length, startIdx + rowsPerPage);
  const pageFiles = finalFiles.slice(startIdx, endIdx);

  return (
    <div>
      <div style={titleRow}>
        <h2 style={{ margin: 0, fontSize: 20 }}>All Files</h2>
        <div style={filterWrapper}>
          <FiSearch aria-hidden style={filterIcon} />
          <input
            type="text"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setFilterText(''); }}
            placeholder={`Filter by ${fieldLabels.join(', ')}`}
            style={filterInput}
          />
          {filterText && (
            <button
              type="button"
              onClick={() => setFilterText('')}
              aria-label="Clear filter"
              title="Clear"
              style={filterClear}
            >
              ✕
            </button>
          )}
        </div>
      </div>
      {/* Toolbar between search and table */}
      <div style={toolbarRow}>
        {/* Left: Download + OCR buttons */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            disabled={selected.size === 0}
            onClick={async () => {
              try {
                const list = Array.from(selected);
                if (!list.length) return;
                const token = (localStorage.getItem('auth_token')||'');
                // Create a progress channel
                const progressId = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now());
                // Immediately show progress UI
                setZipProg({ active: true, processed: 0, total: list.length });
                try { window.spartaToast && window.spartaToast(`Preparing download: 0/${list.length} files`, 'info', 0); } catch(_) {}
                // Subscribe to progress via SSE (token via query since headers aren't supported)
                let es;
                try {
                  es = new EventSource(`${API_URL}/zip-progress/${encodeURIComponent(progressId)}?token=${encodeURIComponent(token)}`);
                  es.onmessage = (ev) => {
                    try {
                      const j = JSON.parse(ev.data || '{}');
                      if (typeof j.processed === 'number' && typeof j.total === 'number') {
                        setZipProg({ active: true, processed: j.processed, total: j.total });
                        try { window.spartaToast && window.spartaToast(`Preparing download: ${Math.min(j.processed, j.total)}/${j.total} files`, 'info', 0); } catch(_) {}
                      }
                      if (j.done) {
                        setZipProg(p => ({ ...p, active: false }));
                        try { es.close(); } catch (_) {}
                      }
                    } catch (_) {}
                  };
                  es.onerror = () => { try { es.close(); } catch (_) {}; };
                } catch (_) {}
                const res = await fetch(`${API_URL}/files/zip?progressId=${encodeURIComponent(progressId)}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                  body: JSON.stringify({ filenames: list })
                });
                if (!res.ok) {
                  const j = await res.json().catch(() => ({}));
                  return alert(j.message || 'Failed to download zip');
                }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const ts = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
                a.download = `files-${ts}.zip`;
                document.body.appendChild(a);
                a.click();
                setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
                setZipProg({ active: false, processed: 0, total: 0 });
                try { window.spartaToast && window.spartaToast(`Download ready: ${list.length} files`, 'success', 3000); } catch(_) {}
              } catch (e) {
                alert('Failed to download zip');
              }
            }}
            title={selected.size === 0 ? 'Select files to enable download' : 'Download selected files as ZIP'}
          >
            Download
          </button>
          <button
            className="btn btn-primary"
            disabled={selected.size === 0 || batchOcr.active}
            onClick={queueSelectedForOcr}
            title={selected.size === 0 ? 'Select files to OCR' : 'Queue selected files for OCR'}
          >
            OCR Selected
          </button>
          {isAdmin && (
            <button
              className="btn btn-primary"
              disabled={selected.size === 0 || pageCounts.loading}
              onClick={handlePageCountClick}
              title={selected.size === 0 ? 'Select files to view page counts' : 'Show page counts for selected files'}
            >
              Page Count
            </button>
          )}
        </div>
        {/* Center: File count (match footer label and style) */}
        <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
          Total Files: {formatCount(finalFiles.length)}
          {t && list.length !== finalFiles.length && (
            <div style={{ fontWeight: 'normal' }}>
              Showing {formatCount(finalFiles.length)} files out of Total Files {formatCount(list.length)}
            </div>
          )}
          {zipProg.active && (
            <div style={{ fontWeight: 'normal', marginTop: 4 }}>
              Zipping {Math.min(zipProg.processed, zipProg.total)} of {zipProg.total}...
            </div>
          )}
          {batchOcr.active && (
            <div style={{ fontWeight: 'normal', marginTop: 4 }}>
              OCR'ing {Math.min(batchOcr.processed, batchOcr.total)} of {batchOcr.total}
              {batchOcr.errors ? ` (${batchOcr.errors} failed)` : ''}...
            </div>
          )}
        </div>
        {/* Right: spacer to center the middle cell */}
        <div />
      </div>
      <div style={scrollContainer}>
        <table style={table}>
          <thead style={theadStyle}>
            <tr>
              <th style={{ ...th, ...thNarrow, borderTopLeftRadius: 8, whiteSpace: 'nowrap', width: 1 }}>Actions</th>
              <th
                style={{ ...th, ...thNarrow, whiteSpace: 'nowrap', width: 1, cursor: 'pointer' }}
                onClick={() => handleSort('__ocr__')}
                title="Sort by OCR status"
              >
                OCR{sortField === '__ocr__' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th style={{ ...th, ...thNarrow, whiteSpace: 'nowrap', width: 1 }}>
                <input
                  type="checkbox"
                  aria-label="Select all visible files"
                  checked={pageFiles.length > 0 && pageFiles.every(f => selected.has(f.filename))}
                  indeterminate={undefined}
                  onChange={(e) => {
                    const allSelected = pageFiles.every(f => selected.has(f.filename));
                    setSelected(prev => {
                      const next = new Set(prev);
                      if (allSelected) { pageFiles.forEach(f => next.delete(f.filename)); }
                      else { pageFiles.forEach(f => next.add(f.filename)); }
                      return next;
                    });
                  }}
                />
              </th>
              <th
                style={{ ...th, whiteSpace: 'nowrap', width: 1, cursor: 'pointer' }}
                onClick={() => handleSort('__created__')}
                title="Sort by created date"
              >
                Created{sortField === '__created__' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
              </th>
              {fieldKeys.map((field, idx, arr) => (
                <th
                  key={field}
                  style={{
                    ...th,
                    cursor: 'pointer',
                    ...(idx === arr.length - 1 ? { borderTopRightRadius: 8 } : {})
                  }}
                  onClick={() => handleSort(field)}
                >
                  {fieldLabels[idx] || field}
                  {sortField === field && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageFiles.map((f, i) => {
              const globalIndex = startIdx + i;
              const parsed = parseFilename(f.filename);
              const isSelected = globalIndex === selectedRow;
              const baseRow = globalIndex % 2 === 0 ? rowEven : rowOdd;
              const rowStyle = isSelected ? { ...baseRow, ...rowSelected } : baseRow;
              return (
                <tr
                  key={globalIndex}
                  style={rowStyle}
                  onClick={() => setSelectedRow(isSelected ? null : globalIndex)}
                  title={f?.filename || ''}
                >
                  <td style={{ ...tdNarrow, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, alignItems: 'center' }}>
                      <a
                        href={`${API_URL}/files/${encodeURIComponent(f.filename)}?token=${encodeURIComponent(getToken())}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-view file-action-btn"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FiEye title="View" />
                      </a>
                      <button
                        onClick={(e) => { e.stopPropagation(); onEmail && onEmail(f.filename); }}
                        className="btn btn-primary file-action-btn"
                      >
                        <FiMail title="Email" />
                      </button>
                      <a
                        href={`${API_URL}/files/${encodeURIComponent(f.filename)}?token=${encodeURIComponent(getToken())}`}
                        download={f.filename}
                        className="btn btn-view file-action-btn"
                        onClick={(e) => e.stopPropagation()}
                        title="Download file"
                      >
                        <FiDownload />
                      </a>
                      <button
                        onClick={(e) => { e.stopPropagation(); openAuditTrail(f.filename); }}
                        className="btn btn-ghost file-action-btn"
                        title="View audit trail"
                      >
                        <FiActivity />
                      </button>
                      {canDelete && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDelName(f.filename); setDelOpen(true); }}
                          className="btn btn-delete file-action-btn"
                          title="Move file to trash"
                        >
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={{ ...tdNarrow, whiteSpace: 'nowrap', width: 1 }}>
                    {(() => {
                      if (f.invalid || f.size === 0) {
                        return (
                          <span
                            title="File is zero bytes or invalid"
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 60, padding: '2px 6px', borderRadius: 12, background: 'var(--ocr-pill-bg)', color: '#b71c1c', fontSize: 12, border: '1px solid #b71c1c33', whiteSpace: 'nowrap', fontWeight: 600 }}
                          >
                            ! Error
                          </span>
                        );
                      }
                      const isLocallyQueued = localQueued.has(f.filename);
                      const hasText = !!f.hasText;
                      // Prioritize processing/queued over hasText so forced re-OCR doesn't show Ready
                      const status = (f.processing ? 'processing' : ((f.queued || isLocallyQueued || f.forced) ? 'queued' : (hasText ? 'ready' : 'missing')));
                      if (status === 'ready') {
                        const readyPillStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 60, padding: '2px 6px', borderRadius: 12, background: 'var(--ocr-pill-bg)', color: '#2e7d32', border: '1px solid #2e7d3233' };
                        return (
                          <span title="Text detected" style={readyPillStyle}>
                            <FiCheckCircle size={14} />
                          </span>
                        );
                      }
                      const color = status === 'processing' ? '#1565c0' : status === 'queued' ? '#ff8f00' : '#b71c1c';
                      const label = status === 'processing' ? 'OCR…' : status === 'queued' ? 'Queued' : 'No Text';
                      const clickable = status === 'missing';
                      const onClick = async (e) => {
                        e.stopPropagation();
                        if (!clickable) return;
                        try {
                          const res = await fetch(`${API_URL}/files/${encodeURIComponent(f.filename)}/ocr`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token')||''}` },
                            body: JSON.stringify({ force: true })
                          });
                          if (res.ok) {
                            setLocalQueued(prev => new Set(prev).add(f.filename));
                          } else {
                            const j = await res.json().catch(() => ({}));
                            alert(j.message || 'Failed to enqueue OCR');
                          }
                        } catch (e) {
                          alert('Failed to enqueue OCR');
                        }
                      };
                      return (
                        <span
                          onClick={onClick}
                          title={clickable ? 'Click to OCR now' : undefined}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 60, padding: '2px 6px', borderRadius: 12, background: 'var(--ocr-pill-bg)', color, fontSize: 12, border: `1px solid ${color}33`, cursor: clickable ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                        >
                          {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ ...tdNarrow, whiteSpace: 'nowrap', width: 1 }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${f.filename}`}
                      checked={selected.has(f.filename)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelected(prev => {
                          const next = new Set(prev);
                          if (checked) next.add(f.filename); else next.delete(f.filename);
                          return next;
                        });
                      }}
                  />
                </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontSize: '12px', fontStyle: 'italic' }}>
                    {formatDateTime(f.createdAt)}
                  </td>
                  {fieldKeys.map((k) => (
                    <td key={k} style={td}>{parsed[k] || ''}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4 + fieldKeys.length} style={tfootStyle}>
                <div style={pagerRow}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label htmlFor="all-rpp" style={{ opacity: 0.85 }}>Rows per page:</label>
                    <select
                      id="all-rpp"
                      value={rowsPerPage}
                      onChange={(e) => { setRowsPerPage(Math.max(1, parseInt(e.target.value || '50', 10) || 50)); setPage(0); }}
                      style={pagerSelect}
                    >
                      {[25, 50, 100, 200].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ fontWeight: 'bold' }}>
                    {finalFiles.length === 0
                      ? 'No files'
                      : `Showing ${formatCount(startIdx + 1)}–${formatCount(endIdx)} of ${formatCount(finalFiles.length)}`}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button className="btn" onClick={() => setPage(0)} disabled={page <= 0}>First</button>
                    <button className="btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page <= 0}>Prev</button>
                    <span style={{ minWidth: 110, textAlign: 'center' }}>Page {Math.min(page + 1, totalPages)} / {totalPages}</span>
                    <button className="btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</button>
                    <button className="btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>Last</button>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {canDelete && (
        <ConfirmModal
          open={delOpen}
          title="Move File to Trash"
          message={`Move "${delName}" to trash? You can restore it later from the Trash view.`}
          confirmLabel="Move to Trash"
          onCancel={() => setDelOpen(false)}
          onConfirm={async () => {
            try {
              if (onDelete) {
                await onDelete(delName);
              } else {
                const token = localStorage.getItem('auth_token') || '';
                await fetch(`${API_URL}/files/${encodeURIComponent(delName)}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
              }
            } catch (_) {}
            setDelOpen(false);
          }}
        />
      )}
      {auditModal.open && (
        <div className="upload-spinner-overlay" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="upload-spinner-box" style={{ width: 720, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <h3 style={{ margin: 0 }}>Audit Trail — {auditModal.name}</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn"
                  onClick={() => {
                    if (!auditPdfUrl) return;
                    try { window.open(auditPdfUrl, '_blank'); } catch (_) {}
                  }}
                  disabled={!auditPdfUrl || auditModal.loading}
                >
                  Download PDF
                </button>
                <button className="btn" onClick={closeAuditTrail}>Close</button>
              </div>
            </div>
            {auditModal.loading ? (
              <div style={{ padding: '24px 0', textAlign: 'center' }}>Loading…</div>
            ) : (
              <>
                {auditModal.error && (
                  <div style={{ color: '#b71c1c', fontWeight: 600, marginTop: 8 }}>{auditModal.error}</div>
                )}
                <div style={{ marginTop: 12, flex: 1, overflowY: 'auto' }}>
                  {sortedAuditEntries.length === 0 ? (
                    <p style={{ fontStyle: 'italic', color: 'var(--text-muted, #666)' }}>No audit entries recorded.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th
                            style={{ textAlign: 'left', padding: '6px 4px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                            onClick={() => changeAuditSort('timestamp')}
                          >
                            When{auditSortIndicator('timestamp')}
                          </th>
                          <th
                            style={{ textAlign: 'left', padding: '6px 4px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                            onClick={() => changeAuditSort('action')}
                          >
                            Action{auditSortIndicator('action')}
                          </th>
                          <th
                            style={{ textAlign: 'left', padding: '6px 4px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                            onClick={() => changeAuditSort('actor')}
                          >
                            Actor{auditSortIndicator('actor')}
                          </th>
                          <th
                            style={{ textAlign: 'left', padding: '6px 4px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                            onClick={() => changeAuditSort('details')}
                          >
                            Details{auditSortIndicator('details')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedAuditEntries.map((entry, idx) => {
                          const metaText = formatAuditMeta(entry.meta);
                          const parts = [];
                          if (entry.ip) parts.push(`IP: ${entry.ip}`);
                          if (metaText) parts.push(metaText);
                          if (entry.raw && !metaText) parts.push(entry.raw);
                          return (
                            <tr key={`${entry.timestamp || idx}-${entry.action || idx}`}>
                              <td style={{ padding: '6px 4px', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>{formatDateTime(entry.timestamp)}</td>
                              <td style={{ padding: '6px 4px', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>{entry.action || '—'}</td>
                              <td style={{ padding: '6px 4px', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>{formatAuditActor(entry)}</td>
                              <td style={{ padding: '6px 4px', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>{parts.length ? parts.join(' | ') : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <PageCountModal
        open={pageCounts.open}
        loading={pageCounts.loading}
        items={pageCounts.items}
        totalPages={pageCounts.totalPages}
        error={pageCounts.error}
        warnings={pageCounts.warnings}
        onClose={closePageCountModal}
      />
    </div>
  );
};

const scrollContainer = {
  maxHeight: 'calc(100vh - 140px)',
  overflowY: 'auto',
  paddingBottom: '50px',
};

const theadStyle = {
  position: 'sticky',
  top: 0,
  backgroundColor: 'var(--table-head-bg)',
  zIndex: 1,
};

const table = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: 10,
  fontSize: '13px',
};

const th = {
  textAlign: 'left',
  padding: '10px 8px',
  backgroundColor: 'var(--table-head-bg)',
  color: 'var(--text)',
  borderBottom: '2px solid var(--border)',
  fontSize: '13px',
  fontWeight: 'bold'
};

const td = {
  padding: '8px',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle',
};

const tfootStyle = {
  padding: '10px',
  textAlign: 'center',
  fontWeight: 'bold',
  backgroundColor: 'var(--table-head-bg)'
};

const pagerRow = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: 12,
};

const pagerSelect = {
  padding: '6px 8px',
  background: 'var(--card-bg)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
};

const rowEven = { backgroundColor: 'var(--row-even-bg)', cursor: 'pointer', transition: 'background-color 120ms ease' };
const rowOdd = { backgroundColor: 'var(--row-odd-bg)', cursor: 'pointer', transition: 'background-color 120ms ease' };
const rowSelected = {
  backgroundColor: 'var(--row-selected-bg, rgba(100,181,246,0.15))',
  boxShadow: 'inset 0 0 0 2px rgba(100,181,246,0.35)',
};

const titleRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

const toolbarRow = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: 10,
  marginTop: 14, // push toolbar (Download + Total) down a bit
  marginBottom: 4,
};

const filterInput = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  padding: '10px 36px 10px 36px',
  borderRadius: 8,
  border: '1.5px solid var(--primary)',
  backgroundColor: 'var(--card-bg)',
  color: 'var(--text)',
  boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
};

const filterWrapper = {
  position: 'relative',
  flex: 1,
  display: 'flex',
};

const filterIcon = {
  position: 'absolute',
  left: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  opacity: 0.7,
  pointerEvents: 'none'
};

const filterClear = {
  position: 'absolute',
  right: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 24,
  height: 24,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--border)',
  background: 'var(--hover-bg)',
  color: 'var(--text)',
  opacity: 0.9,
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  borderRadius: 12,
  zIndex: 1,
};

const viewButton = {
  padding: '6px 12px',
  backgroundColor: 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-block',
};

// Narrow variants for compact columns (Actions/OCR)
const thNarrow = { padding: '8px 8px' };
const tdNarrow = { padding: '6px 8px', verticalAlign: 'middle' };

export default AllFilesView;
